import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMaterials } from '@/hooks/useMaterials';
import { trackOutboundClick } from '@/services/analytics';
import type { PublicMaterial } from '@/services/materials';

const getInStockColorCount = (material: PublicMaterial): number =>
  Object.values(material.colorStock).filter(Boolean).length;

const getTotalColorCount = (materials: readonly PublicMaterial[]): number => {
  const uniqueColors = new Set<string>();
  for (const m of materials) {
    for (const [color, inStock] of Object.entries(m.colorStock)) {
      if (inStock) uniqueColors.add(color);
    }
  }
  return uniqueColors.size;
};

export const HomePage = () => {
  const { data: materials, isLoading } = useMaterials();

  const capabilities = useMemo(() => {
    if (!materials?.length) return [];
    const totalColors = getTotalColorCount(materials);
    const materialNames = materials.map((m) => m.name).join(', ');
    return [
      { value: `${totalColors} Colors`, label: `Across ${materialNames}` },
      { value: `${materials.length} Material${materials.length !== 1 ? 's' : ''}`, label: materialNames },
      { value: '0.4 / 0.6mm', label: 'Nozzle diameter options' },
    ];
  }, [materials]);

  return (
    <div>
      {/* Hero */}
      <section style={{ backgroundColor: 'var(--bg-inverse)' }}>
        <div
          className="container-main text-center hero-section"
          style={{
            paddingTop: 'var(--space-16)',
            paddingBottom: 'var(--space-16)',
          }}
        >
          <h1
            style={{
              fontSize: 'var(--text-hero)',
              fontWeight: 'var(--font-weight-bold)',
              lineHeight: 'var(--leading-tight)',
              letterSpacing: 'var(--tracking-tight)',
              color: 'var(--text-inverse)',
            }}
          >
            Custom{' '}
            <span style={{ color: 'var(--color-primary-400)' }}>3D Printing</span>{' '}
          </h1>
          <p
            style={{
              marginTop: 'var(--space-6)',
              maxWidth: '640px',
              marginLeft: 'auto',
              marginRight: 'auto',
              fontSize: 'var(--text-lg)',
              lineHeight: 'var(--leading-relaxed)',
              color: 'var(--color-neutral-300)',
              fontWeight: 'var(--font-weight-light)',
            }}
          >
            Upload your 3MF files for an instant quote, or browse our catalog of
            ready-made items. Choose from multiple materials and colors with
            0.4mm or 0.6mm nozzle precision.
          </p>
          <div
            className="flex flex-col sm:flex-row items-center justify-center"
            style={{ marginTop: 'var(--space-10)', gap: 'var(--space-4)' }}
          >
            <Link
              to="/quote"
              className="focus-ring hero-cta-button"
              style={{
                backgroundColor: 'var(--color-primary-600)',
                color: 'var(--text-inverse)',
                padding: 'var(--space-3) var(--space-6)',
                borderRadius: 'var(--radius-lg)',
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-weight-semibold)',
                textDecoration: 'none',
                textAlign: 'center',
                transition: `background-color var(--duration-fast) var(--ease-out)`,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-primary-500)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-primary-600)'; }}
            >
              Get an Instant Quote
            </Link>
            <Link
              to="/catalog"
              className="focus-ring hero-cta-button"
              style={{
                border: '1px solid var(--color-neutral-600)',
                color: 'var(--text-inverse)',
                padding: 'var(--space-3) var(--space-6)',
                borderRadius: 'var(--radius-lg)',
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-weight-semibold)',
                textDecoration: 'none',
                textAlign: 'center',
                transition: `background-color var(--duration-fast) var(--ease-out)`,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-neutral-800)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              Browse Catalog
            </Link>
          </div>
        </div>
      </section>

      {/* Capabilities strip */}
      {capabilities.length > 0 && (
        <section
          style={{
            backgroundColor: 'var(--bg-surface)',
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          <div
            className="container-main grid sm:grid-cols-3"
            style={{
              paddingTop: 'var(--space-8)',
              paddingBottom: 'var(--space-8)',
              gap: 'var(--space-6)',
              textAlign: 'center',
            }}
          >
            {capabilities.map((cap) => (
              <div key={cap.label}>
                <p
                  style={{
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 'var(--font-weight-bold)',
                    color: 'var(--text-primary)',
                    letterSpacing: 'var(--tracking-tight)',
                  }}
                >
                  {cap.value}
                </p>
                <p
                  style={{
                    marginTop: 'var(--space-1)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-secondary)',
                  }}
                >
                  {cap.label}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* How It Works */}
      <section
        className="container-main section-padding"
        style={{
          paddingTop: 'var(--space-20)',
          paddingBottom: 'var(--space-20)',
        }}
      >
        <h2
          className="text-center"
          style={{
            fontSize: 'var(--text-3xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--text-primary)',
            letterSpacing: 'var(--tracking-tight)',
          }}
        >
          How It Works
        </h2>
        <div
          className="grid md:grid-cols-3"
          style={{
            marginTop: 'var(--space-12)',
            gap: 'var(--space-8)',
          }}
        >
          {STEPS.map((step) => (
            <div key={step.title} className="text-center">
              <div
                className="mx-auto flex items-center justify-center"
                style={{
                  height: '3.5rem',
                  width: '3.5rem',
                  borderRadius: 'var(--radius-full)',
                  backgroundColor: 'var(--bg-accent-subtle)',
                  color: 'var(--text-accent)',
                  fontSize: 'var(--text-xl)',
                  fontWeight: 'var(--font-weight-bold)',
                }}
              >
                {step.number}
              </div>
              <h3
                style={{
                  marginTop: 'var(--space-4)',
                  fontSize: 'var(--text-xl)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--text-primary)',
                }}
              >
                {step.title}
              </h3>
              <p
                style={{
                  marginTop: 'var(--space-2)',
                  color: 'var(--text-secondary)',
                  lineHeight: 'var(--leading-relaxed)',
                }}
              >
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* No Model CTA */}
      <section
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderTop: '1px solid var(--border-subtle)',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <div
          className="container-main text-center"
          style={{
            paddingTop: 'var(--space-12)',
            paddingBottom: 'var(--space-12)',
            maxWidth: '640px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <h2
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--text-primary)',
              letterSpacing: 'var(--tracking-tight)',
            }}
          >
            Don't have a model yet?
          </h2>
          <p
            style={{
              marginTop: 'var(--space-3)',
              color: 'var(--text-secondary)',
              lineHeight: 'var(--leading-relaxed)',
            }}
          >
            Browse thousands of free, ready-to-print designs on MakerWorld.
            Find something you like, download the file, and come back to get it
            printed and shipped to your door.
          </p>
          <a
            href="https://makerworld.com/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackOutboundClick('https://makerworld.com/', 'Browse MakerWorld')}
            className="focus-ring hero-cta-button"
            style={{
              display: 'inline-block',
              marginTop: 'var(--space-6)',
              backgroundColor: 'var(--color-primary-600)',
              color: 'var(--text-inverse)',
              padding: 'var(--space-3) var(--space-6)',
              borderRadius: 'var(--radius-lg)',
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-weight-semibold)',
              textDecoration: 'none',
              textAlign: 'center',
              transition: `background-color var(--duration-fast) var(--ease-out)`,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-primary-500)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-primary-600)'; }}
          >
            Browse MakerWorld
          </a>
        </div>
      </section>

      {/* Materials */}
      <section style={{ backgroundColor: 'var(--bg-surface-sunken)' }}>
        <div
          className="container-main section-padding"
          style={{
            paddingTop: 'var(--space-20)',
            paddingBottom: 'var(--space-20)',
          }}
        >
          <h2
            className="text-center"
            style={{
              fontSize: 'var(--text-3xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--text-primary)',
              letterSpacing: 'var(--tracking-tight)',
            }}
          >
            Materials We Offer
          </h2>
          {isLoading ? (
            <p
              className="text-center"
              style={{
                marginTop: 'var(--space-12)',
                color: 'var(--text-secondary)',
              }}
            >
              Loading materials...
            </p>
          ) : materials?.length ? (
            <div
              className="grid sm:grid-cols-2 lg:grid-cols-3"
              style={{
                marginTop: 'var(--space-12)',
                gap: 'var(--space-6)',
              }}
            >
              {materials.map((material) => {
                const colorCount = getInStockColorCount(material);
                return (
                  <div
                    key={material.materialId}
                    style={{
                      backgroundColor: 'var(--bg-surface)',
                      padding: 'var(--space-6)',
                      borderRadius: 'var(--radius-lg)',
                      boxShadow: 'var(--shadow-sm)',
                      transition: `box-shadow var(--duration-normal) var(--ease-out)`,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
                  >
                    <h3
                      style={{
                        fontSize: 'var(--text-lg)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--text-primary)',
                      }}
                    >
                      {material.name}
                    </h3>
                    <p
                      style={{
                        marginTop: 'var(--space-2)',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--text-secondary)',
                        lineHeight: 'var(--leading-relaxed)',
                      }}
                    >
                      {material.description}
                    </p>
                    {colorCount > 0 && (
                      <p
                        style={{
                          marginTop: 'var(--space-3)',
                          fontSize: 'var(--text-xs)',
                          color: 'var(--text-secondary)',
                          fontWeight: 'var(--font-weight-medium)',
                        }}
                      >
                        {colorCount} color{colorCount !== 1 ? 's' : ''} available
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p
              className="text-center"
              style={{
                marginTop: 'var(--space-12)',
                color: 'var(--text-secondary)',
              }}
            >
              No materials available at this time.
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

const STEPS = [
  {
    number: 1,
    title: 'Upload Your File',
    description:
      'Upload your 3MF file and get an instant price estimate based on volume, material, and complexity.',
  },
  {
    number: 2,
    title: 'Choose Options',
    description:
      'Select your material and color. Each option comes with detailed specs and an accurate price.',
  },
  {
    number: 3,
    title: 'Receive Your Print',
    description:
      'We print and ship your order. Track progress in real-time through your dashboard.',
  },
] as const;
